/**
 * The document registry. Adding a document is one file plus one line here — nothing shared is
 * edited, and `Printing.print` picks it up by kind.
 */
import type { BillInput, DocumentKind, InputByKind, Plan, PrintContext, TicketsInput } from "../types";
import { planBill } from "./bill";
import { planDrawer } from "./drawer";
import { planLabel } from "./label";
import { planRaw } from "./raw";
import { planSalesSummary } from "./salesSummary";
import { planTestPrint } from "./testPrint";
import { planTickets } from "./ticket";

type Planners = { [K in DocumentKind]: (input: InputByKind[K], ctx: PrintContext) => Plan };

export const DOCUMENTS: Planners = {
  bill: (input, ctx) => planBill(input, ctx),
  tickets: (input, ctx) => planTickets(input, ctx),
  label: (input, ctx) => planLabel(input, ctx),
  salesSummary: (input, ctx) => planSalesSummary(input, ctx),
  testPrint: (input) => planTestPrint(input),
  raw: (input, ctx) => planRaw(input, ctx),
  cashDrawer: (input, ctx) => planDrawer(input, ctx),
};

/**
 * v1 `handlePrintLogic` — what a CLOSED sale prints: the bill, when this device has a default
 * printer or relays to a master, with the channel's copy count; then its pending tickets.
 *
 * This is the payment engine's `effects.print`. Saving a sale prints tickets only
 * (`effects.printTickets`), and the auto-persist after an external payment prints nothing.
 */
export function planSaleClosed(sale: any, ctx: PrintContext): { bill?: BillInput; tickets: TicketsInput } {
  const printer = ctx.printers.defaultPrinter || {};
  // v1: `if (printer?.mid || isSlave() || isPineLabDevice())` — a built-in printer needs no record
  return {
    bill: printer.mid || ctx.device.isSlave || ctx.device.builtInPrinter ? { sale, printer, copiesFromChannel: true } : undefined,
    tickets: { sale, tickets: sale?.tickets || [] },
  };
}
